import { useLayoutEffect, useMemo } from 'react'

import { ImagesGetRequest } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery, useMutationState, useQuery } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'
import { pickBy } from 'lodash-es'

import { imageObjectMutationKeys } from 'hooks/mutations/content'
import { imagesOverviewRoute, imagesRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Routing'
import { imageObjectQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ setLoadingRows }: UseAsyncContentCaching) => {
  const { workspaceId } = imagesRoute.useParams()
  const search = imagesOverviewRoute.useSearch()
  const { query } = search

  const sortProperties = useMemo(
    () => pickBy(search, (_, key) => key.startsWith('orderBy')) as PickStartsWith<ImagesGetRequest, 'orderBy'>,
    [search],
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const imagesQuery = useInfiniteQuery({
    ...imageObjectQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId as string,
      query,
      ...sortProperties,
    }),
    enabled: workspaceQuery.isSuccess && !!workspaceQuery.data.contentRepositoryId,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const pendingImages = useMutationState({
    filters: { mutationKey: imageObjectMutationKeys.delete(), status: 'pending' },
    select: (mutation) => mutation.state.variables as { imageId: string },
  })

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingImages.map((task) => task.imageId)])])
  }, [pendingImages, setLoadingRows])

  return {
    workspaceQuery,
    imagesQuery,
  }
}
